"use client";

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {zodResolver} from '@hookform/resolvers/zod';
import {useForm} from 'react-hook-form';
import {z} from 'zod';
import {useRouter} from "next/navigation";
import {Button} from "@/components/ui/button";
import {useDropzone} from "react-dropzone";
import {ChevronRight} from "lucide-react";
import {useMutation, useQuery} from "@tanstack/react-query";
import {getLeadSourceValue} from "@/utils/leadSource";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import StepDettagliGenerali from "@/components/vendi/form-permuta-steps/StepDettagliGenerali";
import StepRichiesteVeicolo from "@/components/vendi/form-permuta-steps/StepRichiesteVeicolo";
import StepCondizioniVeicolo from "@/components/vendi/form-permuta-steps/StepCondizioniVeicolo";
import StepDatiCliente from "@/components/vendi/form-permuta-steps/StepDatiCliente";
import StepFotoAuto from "@/components/vendi/form-permuta-steps/StepFotoAuto";

// Tipi
interface DettagliGenerali {
    marca: string;
    anno: string;
    mese: string;
    modello: string;
    versione: string;
    email: string;
    chilometri: string;
    gpic: string;
    idUtente: string;
}

// Schema di validazione
const schemaDettagliGenerali = z.object({
    marca: z.string().min(1, {message: 'La marca è obbligatoria.'}),
    anno: z.string().min(1, {message: 'L\'anno è obbligatorio.'}),
    mese: z.string().min(1, {message: 'Il mese è obbligatorio.'}),
    modello: z.string().min(1, {message: 'Il modello è obbligatorio.'}),
    versione: z.string().min(1, {message: 'La versione è obbligatoria.'}),
    email: z.string().email({message: 'L\'email non è valida.'}),
    chilometri: z.string().min(1, {message: 'I chilometri sono obbligatori.'}).regex(/^[0-9]*$/, {message: 'I chilometri devono essere un numero.'}),
    permuta: z.string().min(1, {message: 'La scelta è obbligatoria.'}),
});

// Tipi
interface RichiesteVeicolo {
    reqBollo: string;
    reqNChiavi: string;
    reqSetGomme: string;
    reqLibroMan: string;
    reqRevisione: string;
    reqPrivato: string;
    reqImportata: string;
    reqColoreEsterno: string;
    reqColoreSedili: string;
    reqMaterialeSedili: string;
}

// Schema di validazione
const schemaRichiesteVeicolo = z.object({
    reqBollo: z.string().min(1, {message: 'Il bollo è obbligatorio.'}),
    reqNChiavi: z.string().min(1, {message: 'Il numero di chiavi è obbligatorio.'}),
    reqSetGomme: z.string().min(1, {message: 'Il set di gomme è obbligatorio.'}),
    reqLibroMan: z.string().min(1, {message: 'Il libro manutenzione è obbligatorio.'}),
    reqRevisione: z.string().min(1, {message: 'La revisione è obbligatoria.'}),
    reqPrivato: z.string().min(1, {message: 'Il privato è obbligatorio.'}),
    reqImportata: z.string().min(1, {message: 'L\'importazione è obbligatoria.'}),
    reqColoreEsterno: z.string().min(1, {message: 'Il colore esterno è obbligatorio.'}),
    reqColoreSedili: z.string().min(1, {message: 'Il colore dei sedili è obbligatorio.'}),
    reqMaterialeSedili: z.string().min(1, {message: 'Il materiale dei sedili è obbligatorio.'}),
});


// Tipi
interface CondizioniVeicolo {
    reqAntSin?: string;
    reqAssLiquid?: string;
    reqIncidenti: string;
    reqRiparati?: string;
    reqInterni: string;
    reqEsterni: string;
    reqSpie: string;
    reqUsura: string;
    reqMotore: string;
    reqCambio: string;
    reqSterzo?: string;
    reqSospensioni?: string;
    reqFreni?: string;
    reqFrizione: string;
    reqCondizionatore: string;
}

// Schema di validazione
const schemaCondizioniVeicolo = z.object({
    reqIncidenti: z.string().min(1, {message: 'Gli incidenti sono obbligatori.'}),
    reqInterni: z.string().min(1, {message: 'Gli interni sono obbligatori.'}),
    reqEsterni: z.string().min(1, {message: 'Gli esterni sono obbligatori.'}),
    reqSpie: z.string().min(1, {message: 'Le spie sono obbligatorie.'}),
    reqUsura: z.string().min(1, {message: 'L\'usura è obbligatoria.'}),
    reqMotore: z.string().min(1, {message: 'Il motore è obbligatorio.'}),
    reqCambio: z.string().min(1, {message: 'Il cambio è obbligatorio.'}),
    reqFrizione: z.string().min(1, {message: 'La frizione è obbligatoria.'}),
    reqCondizionatore: z.string().min(1, {message: 'Il condizionatore è obbligatorio.'}),
});

// Tipi
interface DatiCliente {
    valutazione: string;
    nome: string;
    cognome: string;
    targa: string;
    telefono: string;
    foto?: string;
    provincia: string;
    note?: string;
}

type SendPermutaResponse = {
    status: "success" | "error";
    message: string;
    data?: {
        productId?: number | null;
        codiceLead?: string | null;
        idPermuta?: number | null;
        APERMUTA?: string | null;
        valutazioneStimata?: unknown;
    };
};

type EurotaxValutazioneApiResponse = {
    valutazione?: Record<string, unknown> | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value)
);

const toNullableString = (value: unknown): string | null => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized.length > 0 ? normalized : null;
};

const toNullableNumber = (value: unknown): number | null => {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === "string") {
        const normalized = value.trim().replace(",", ".");
        if (!normalized) return null;
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
};

const toStringArray = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];

    return value
        .map((item) => toNullableString(item))
        .filter((item): item is string => item !== null);
};

const firstFiniteNumber = (...values: Array<number | null | undefined>): number | null => {
    for (const value of values) {
        if (typeof value === "number" && Number.isFinite(value)) {
            return value;
        }
    }

    return null;
};

const pickNumericField = (source: Record<string, unknown>, keys: string[]): number | null => {
    for (const key of keys) {
        const parsed = toNullableNumber(source[key]);
        if (parsed !== null) {
            return parsed;
        }
    }

    return null;
};

const parseEurotaxValutazione = (payload: unknown): EurotaxValutazione | null => {
    if (!isRecord(payload) || !isRecord(payload.valutazione)) {
        return null;
    }

    const source = payload.valutazione;
    const fonteCoefficiente = toNullableString(source.fonteCoefficiente);

    return {
        ...source,
        codiceMotornetUnivoco: toNullableString(source.codiceMotornetUnivoco),
        codiceCostruttore: toNullableString(source.codiceCostruttore),
        codiciCostruttoreAlternativi: toStringArray(source.codiciCostruttoreAlternativi),
        codiciCostruttoreRicerca: toStringArray(source.codiciCostruttoreRicerca),
        coefficienteValutazione: toNullableNumber(source.coefficienteValutazione),
        fonteCoefficiente: fonteCoefficiente === "db" || fonteCoefficiente === "streetPriceMin"
            ? fonteCoefficiente
            : null,
        valoreRiferimento: toNullableNumber(source.valoreRiferimento),
        quotazioneProposta: toNullableNumber(source.quotazioneProposta),
        margineAggiuntivoDesiderato: toNullableNumber(source.margineAggiuntivoDesiderato),
        margineDbDiagnostico: toNullableNumber(source.margineDbDiagnostico),
        rigaEurotaxDb: isRecord(source.rigaEurotaxDb) ? source.rigaEurotaxDb : null,
        streetPriceMinimoFallback: toNullableNumber(source.streetPriceMinimoFallback),
        min: pickNumericField(source, ["min", "valoreMinimo", "quotazioneMinima", "prezzoMinimo"]),
        max: pickNumericField(source, ["max", "valoreMassimo", "quotazioneMassima", "prezzoMassimo"]),
    };
};

// Schema di validazione
const schemaDatiCliente = z.object({
    valutazione: z.string().min(1, {message: 'La valutazione richiesta è obbligatoria.'}),
    nome: z.string().min(1, {message: 'Il nome è obbligatorio.'}),
    cognome: z.string().min(1, {message: 'Il cognome è obbligatorio.'}),
    targa: z.string().min(7, {message: 'La targa deve essere di 7 caratteri.'}).max(7, {message: 'La targa deve essere di 7 caratteri.'}).regex(/^[A-Za-z]{2}\d{3}[A-Za-z]{2}$/, {message: 'La targa non è valida.'}),
    telefono: z.string()
        .trim()
        .min(1, {message: 'Il telefono \u00e8 obbligatorio.'})
        .length(10, {message: 'Il telefono deve essere di 10 cifre.'})
        .regex(/^[0-9]+$/, {message: 'Il telefono deve essere un numero.'}),
    provincia: z.string().min(1, {message: 'La provincia è obbligatoria.'}),
    note: z.string().optional(),
});


interface FormPermutaProps {
    title?: string;
    embeddedMode?: boolean;
    forcePermutaOnly?: boolean;
    initialCustomerData?: {
        nome?: string;
        cognome?: string;
        email?: string;
        telefono?: string;
        provincia?: string;
    };
    onCompleted?: (payload: {
        permutaId?: string;
        productId?: string;
        APERMUTA?: string;
        codiceLead?: string;
        permutaLeadCode?: string;
        valoreDesiderato?: string;
        targa?: string;
        marca?: string;
        modello?: string;
        versione?: string;
        meseImmatricolazione?: string;
        annoImmatricolazione?: string;
        km?: string;
        valutazioneStimata?: PermutaData["valutazioneStimata"];
        permutaCompleted: boolean;
    }) => void;
}

const FormPermuta: React.FC<FormPermutaProps> = ({
    title = 'Inserisci i dati della tua auto',
    embeddedMode = false,
    forcePermutaOnly = false,
    initialCustomerData,
    onCompleted,
}) => {
    const router = useRouter();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const isSubmittingRef = useRef(false);
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState("");
    const [photos, setPhotos] = useState<{ file?: File | null, url: string | null, isLoading: boolean }[]>([]);

    const [valutezione, setValutezione] = useState<{ min: number; max: number } | null>(null);
    const [eurotaxValutazione, setEurotaxValutazione] = useState<EurotaxValutazione | null>(null);
    const [isManualValutazioneOverride, setIsManualValutazioneOverride] = useState(false);

    const [productId, setProductId] = useState<string>("");
    const [permutaId, setPermutaId] = useState<string>("");
    const [codiceLead, setCodiceLead] = useState<string>("");
    const permutaIdRef = useRef<string>("");
    const aPermutaRef = useRef<string>("");
    const codiceLeadRef = useRef<string>("");
    const [veichleType, setVeichleType] = useState<"auto" | "vcom">("auto");
    const [selectedSede, setSelectedSede] = useState<string>("");
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>("");
    const [mesi, setMesi] = useState<{ value: string; label: string; }[]>([]);
    const [eurotaxWarning, setEurotaxWarning] = useState<string>("");
    const minNumberOfPhotos = embeddedMode ? 0 : 5;
    const eurotaxBaseUrl = (process.env.NEXT_PUBLIC_API_URL || "").trim().replace(/\/$/, "");
    const monthOptions = useMemo(() => ([
        {value: '1', label: 'Gennaio'},
        {value: '2', label: 'Febbraio'},
        {value: '3', label: 'Marzo'},
        {value: '4', label: 'Aprile'},
        {value: '5', label: 'Maggio'},
        {value: '6', label: 'Giugno'},
        {value: '7', label: 'Luglio'},
        {value: '8', label: 'Agosto'},
        {value: '9', label: 'Settembre'},
        {value: '10', label: 'Ottobre'},
        {value: '11', label: 'Novembre'},
        {value: '12', label: 'Dicembre'},
    ]), []);

    const uploadPhotoMutation = useMutation({
        mutationFn: async (file: File): Promise<string> => {
            const formData = new FormData();
            formData.append("file", file);
            const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL_ASTA}/api/v1/store/upload-file`, {
                method: "POST",
                body: formData,
            });
            if (!response.ok) {
                throw new Error("Errore nel caricamento della foto");
            }
            const data = await response.json();
            return data.url;
        }
    });

    const uploadPhoto = async (file: File) => {
        try {
            return await uploadPhotoMutation.mutateAsync(file);
        } catch (error) {
            console.error("Errore nel caricamento della foto", error);
            return null;
        }
    };

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        const newPhotos = acceptedFiles.map(file => ({
            file,
            url: null,
            isLoading: true
        }));

        setPhotos(prevPhotos => [...prevPhotos, ...newPhotos]);

        const uploadedPhotos = await Promise.all(
            newPhotos.map(async (photo) => {
                const url = await uploadPhoto(photo.file);
                return { ...photo, url, isLoading: false };
            })
        );

        setPhotos(prevPhotos =>
            prevPhotos.map(photo => uploadedPhotos.find(p => p.file === photo.file) || photo)
        );
    }, [uploadPhoto]);

    const removePhoto = (index: number) => {
        setPhotos((prevPhotos) => prevPhotos.filter((_, i) => i !== index));
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "image/*": [] },
        maxFiles: 20,
    });

    const formDettagliGenerali = useForm({
        resolver: zodResolver(schemaDettagliGenerali),
        mode: "onTouched",
        defaultValues: {
            marca: '',
            anno: '',
            mese: '',
            modello: '',
            versione: '',
            email: initialCustomerData?.email ?? '',
            chilometri: '',
            permuta: forcePermutaOnly ? 'Permuta' : '',
        }
    });

    const formRichiesteVeicolo = useForm({
        resolver: zodResolver(schemaRichiesteVeicolo),
        mode: "onTouched",
        defaultValues: {
            reqBollo: '',
            reqNChiavi: '',
            reqSetGomme: '',
            reqLibroMan: '',
            reqRevisione: '',
            reqPrivato: '',
            reqImportata: '',
            reqColoreEsterno: '',
            reqColoreSedili: '',
            reqMaterialeSedili: '',
        }
    });

    const formCondizioniVeicolo = useForm({
        resolver: zodResolver(schemaCondizioniVeicolo),
        mode: "onTouched",
        defaultValues: {
            reqIncidenti: '',
            reqInterni: '',
            reqEsterni: '',
            reqSpie: '',
            reqUsura: '',
            reqMotore: '',
            reqCambio: '',
            reqFrizione: '',
            reqCondizionatore: '',
        }
    });

    const formDatiCliente = useForm({
        resolver: zodResolver(schemaDatiCliente),
        mode: "onTouched",
        defaultValues: {
            valutazione: '',
            nome: initialCustomerData?.nome ?? '',
            cognome: initialCustomerData?.cognome ?? '',
            targa: '',
            telefono: initialCustomerData?.telefono ?? '',
            provincia: initialCustomerData?.provincia ?? '',
            note: '',
        }
    });
    const handleFinalSubmit = async ({includePhotos = permuta === "Permuta"}: { includePhotos?: boolean } = {}) => {
        const areCustomerDataValid = await formDatiCliente.trigger([
            "nome",
            "cognome",
            "targa",
            "telefono",
            "provincia",
        ]);
        if (!areCustomerDataValid) return;

        if (
            usesEurotaxValutazioneFlow &&
            hasCompletedClientProfile &&
            (!isValutazioneRevealReady || eurotaxValutazioneQuery.isLoading)
        ) {
            setDialogMessage("Attendi il completamento della valutazione prima di inviare.");
            setOpenDialog(true);
            return;
        }

        if (shouldRequireManualValutazione()) {
            const isValutazioneValid = await formDatiCliente.trigger("valutazione");
            if (!isValutazioneValid) {
                setDialogMessage("Indica quanto vorresti ricavare prima di inviare.");
                setOpenDialog(true);
                return;
            }
        }

        if (includePhotos && photos.some(photo => photo.isLoading)) {
            setDialogMessage("Attendi il completamento del caricamento delle foto.");
            setOpenDialog(true);
            return;
        }

        const params = buildPermutaPayload({
            step: includePhotos ? (stepNames[5] || "foto-auto") : (stepNames[4] || "dati-cliente"),
            includePhotos,
            includeAppuntamento: false,
        });
        const isSuccess = await submitPermutaPayload(params);

        if (!isSuccess) return;
        await redirectToThankYou();
    };

    const marca = formDettagliGenerali.watch("marca");
    const anno = formDettagliGenerali.watch("anno");
    const mese = formDettagliGenerali.watch("mese");
    const modello = formDettagliGenerali.watch("modello");
    const versione = formDettagliGenerali.watch("versione");
    const chilometri = formDettagliGenerali.watch("chilometri");
    const permuta = formDettagliGenerali.watch("permuta");
    const email = formDettagliGenerali.watch("email");
    const nomeCliente = formDatiCliente.watch("nome");
    const cognomeCliente = formDatiCliente.watch("cognome");
    const telefonoCliente = formDatiCliente.watch("telefono");
    const provinciaCliente = formDatiCliente.watch("provincia");
    const targaCliente = formDatiCliente.watch("targa");
    const [isValutazioneRevealReady, setIsValutazioneRevealReady] = useState(false);
    const valutazioneRevealTimeoutRef = useRef<number | null>(null);
    const hasCompletedClientProfile = useMemo(() => (
        [nomeCliente, cognomeCliente, telefonoCliente, provinciaCliente, targaCliente]
            .every((value) => String(value ?? "").trim().length > 0)
    ), [cognomeCliente, nomeCliente, provinciaCliente, targaCliente, telefonoCliente]);
    const usesEurotaxValutazioneFlow = permuta === "Permuta" || permuta === "Vendere";
    const hasPositiveQuotazioneProposta = typeof eurotaxValutazione?.quotazioneProposta === "number"
        && Number.isFinite(eurotaxValutazione.quotazioneProposta)
        && eurotaxValutazione.quotazioneProposta > 0;
    const shouldRequireManualValutazione = () => usesEurotaxValutazioneFlow
        && hasCompletedClientProfile
        && isValutazioneRevealReady
        && !eurotaxValutazioneQuery.isLoading
        && !hasPositiveQuotazioneProposta;
    const shouldHideValutazioneSection = hasPositiveQuotazioneProposta;
    const finalStep = permuta === "Permuta" ? 5 : 4;

    useEffect(() => {
        if (!forcePermutaOnly) return;
        if (formDettagliGenerali.getValues("permuta") !== "Permuta") {
            formDettagliGenerali.setValue("permuta", "Permuta", { shouldValidate: true });
        }
    }, [forcePermutaOnly, formDettagliGenerali]);

    const fetchEurotaxJson = useCallback(async <T,>(path: string, fallback: T): Promise<T> => {
        if (!eurotaxBaseUrl) {
            setEurotaxWarning("I dati del veicolo non sono disponibili in questo momento. Riprova tra poco.");
            return fallback;
        }

        try {
            const response = await fetch(`${eurotaxBaseUrl}${path}`, { cache: "no-store" });
            if (!response.ok) {
                setEurotaxWarning("I dati del veicolo non sono disponibili in questo momento. Riprova tra poco.");
                return fallback;
            }

            setEurotaxWarning("");
            return await response.json();
        } catch {
            setEurotaxWarning("I dati del veicolo non sono disponibili in questo momento. Riprova tra poco.");
            return fallback;
        }
    }, [eurotaxBaseUrl]);

    const fetchEurotaxValutazione = useCallback(async (): Promise<EurotaxValutazione | null> => {
        if (!eurotaxBaseUrl) {
            return null;
        }

        const response = await fetch(`${eurotaxBaseUrl}/api/eurotax/valutazione`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            cache: "no-store",
            body: JSON.stringify({
                tipo: veichleType,
                codiceMotornetUnivoco: versione,
                anno,
                mese: mese.padStart(2, "0"),
                km: chilometri,
                accessori: [],
            }),
        });

        if (!response.ok) {
            throw new Error("Failed to fetch Eurotax valuation");
        }

        const payload = await response.json().catch(() => null) as EurotaxValutazioneApiResponse | null;
        return parseEurotaxValutazione(payload);
    }, [anno, chilometri, eurotaxBaseUrl, mese, veichleType, versione]);

    const sediQuery = useQuery({
        queryKey: ['dove-siamo'],
        enabled: currentStep === 6,
        retry: false,
        queryFn: async () => {
            const response = await fetch('/api/dove-siamo');
            if (!response.ok) {
                throw new Error('Errore nel caricamento delle sedi');
            }
            const data = await response.json();
            return data.locations || [];
        },
    });

    const marcheQuery = useQuery({
        queryKey: ['eurotax-makes', veichleType],
        retry: false,
        queryFn: async () => {
            return fetchEurotaxJson(`/api/eurotax/makes?type=${veichleType}`, { marche: [] });
        },
    });

    const anniQuery = useQuery({
        queryKey: ['eurotax-years', marca, veichleType],
        enabled: !!marca,
        retry: false,
        queryFn: async () => {
            return fetchEurotaxJson(`/api/eurotax/years/${encodeURIComponent(marca)}?type=${veichleType}`, { anni: [] });
        },
    });

    const modelliQuery = useQuery({
        queryKey: ['eurotax-models', marca, anno, veichleType],
        enabled: !!marca && !!anno,
        retry: false,
        queryFn: async () => {
            return fetchEurotaxJson(
                `/api/eurotax/models/${encodeURIComponent(marca)}/${encodeURIComponent(anno)}?type=${veichleType}`,
                { modelli: [] }
            );
        },
    });

    const versioniQuery = useQuery({
        queryKey: ['eurotax-versions', modello, anno, veichleType],
        enabled: !!modello && !!anno,
        retry: false,
        queryFn: async () => {
            return fetchEurotaxJson(
                `/api/eurotax/versions/${encodeURIComponent(modello)}/${encodeURIComponent(anno)}?type=${veichleType}`,
                { versioni: [] }
            );
        },
    });

    const eurotaxValutazioneQuery = useQuery({
        queryKey: ['eurotax-valutazione', veichleType, versione, anno, mese, chilometri],
        enabled: (
            currentStep >= 4 &&
            usesEurotaxValutazioneFlow &&
            hasCompletedClientProfile &&
            !!versione &&
            !!anno &&
            !!mese &&
            !!chilometri
        ),
        retry: false,
        queryFn: fetchEurotaxValutazione,
    });

    const marche: any[] = marcheQuery.data?.marche ?? [];
    const anni = anniQuery.data?.anni?.map(String) ?? [];
    const modelli: any[] = modelliQuery.data?.modelli ?? [];
    const versioni: any[] = versioniQuery.data?.versioni ?? [];
    const sedi = sediQuery.data ?? [];

    useEffect(() => {
        if (sediQuery.error) {
            console.error("Errore nel caricamento delle sedi:", sediQuery.error);
        }
    }, [sediQuery.error]);

    useEffect(() => {
        if (marcheQuery.error) {
            console.warn("Eurotax makes unavailable:", marcheQuery.error);
        }
    }, [marcheQuery.error]);

    useEffect(() => {
        if (anniQuery.error) {
            console.warn("Eurotax years unavailable:", anniQuery.error);
        }
    }, [anniQuery.error]);

    useEffect(() => {
        if (modelliQuery.error) {
            console.warn("Eurotax models unavailable:", modelliQuery.error);
        }
    }, [modelliQuery.error]);

    useEffect(() => {
        if (versioniQuery.error) {
            console.warn("Eurotax versions unavailable:", versioniQuery.error);
        }
    }, [versioniQuery.error]);

    useEffect(() => {
        if (eurotaxValutazioneQuery.error) {
            console.warn("Eurotax valuation unavailable:", eurotaxValutazioneQuery.error);
        }
    }, [eurotaxValutazioneQuery.error]);

    useEffect(() => {
        setEurotaxValutazione(eurotaxValutazioneQuery.data ?? null);
    }, [eurotaxValutazioneQuery.data]);

    useEffect(() => {
        if (process.env.NODE_ENV === "production" || !eurotaxValutazione) return;

        console.log("[eurotax-valutazione]", {
            codiceMotornetUnivoco: eurotaxValutazione.codiceMotornetUnivoco ?? versione,
            codiceCostruttore: eurotaxValutazione.codiceCostruttore,
            codiciCostruttoreAlternativi: eurotaxValutazione.codiciCostruttoreAlternativi ?? [],
            codiciCostruttoreRicerca: eurotaxValutazione.codiciCostruttoreRicerca ?? [],
            fonteCoefficiente: eurotaxValutazione.fonteCoefficiente,
            coefficienteValutazione: eurotaxValutazione.coefficienteValutazione,
            quotazioneProposta: eurotaxValutazione.quotazioneProposta,
        });
    }, [eurotaxValutazione, versione]);

    const previousVehicleTypeRef = useRef<"auto" | "vcom" | undefined>(undefined);
    const previousValutazioneVehicleKeyRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        const vehicleKey = [veichleType, versione, anno, mese, chilometri, permuta].join("|");

        if (previousValutazioneVehicleKeyRef.current === undefined) {
            previousValutazioneVehicleKeyRef.current = vehicleKey;
            return;
        }

        if (previousValutazioneVehicleKeyRef.current === vehicleKey) return;

        previousValutazioneVehicleKeyRef.current = vehicleKey;
        setEurotaxValutazione(null);
        setIsManualValutazioneOverride(false);
        formDatiCliente.setValue("valutazione", "", {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
        });
    }, [anno, chilometri, formDatiCliente, mese, permuta, veichleType, versione]);

    useEffect(() => {
        if (previousVehicleTypeRef.current === undefined) {
            previousVehicleTypeRef.current = veichleType;
            return;
        }
        if (previousVehicleTypeRef.current === veichleType) return;
        previousVehicleTypeRef.current = veichleType;
        formDettagliGenerali.reset({
            ...formDettagliGenerali.getValues(),
            marca: '',
            anno: '',
            mese: '',
            modello: '',
            versione: '',
        });
        setMesi([]);
    }, [formDettagliGenerali, veichleType]);

    const previousMarcaRef = useRef<string | undefined>(undefined);
    const previousAnnoRef = useRef<string | undefined>(undefined);
    const previousModelloRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (previousMarcaRef.current === undefined) {
            previousMarcaRef.current = marca;
            setMesi(marca ? monthOptions : []);
            return;
        }
        if (previousMarcaRef.current === marca) return;
        previousMarcaRef.current = marca;
        if (!marca) {
            setMesi([]);
            return;
        }
        formDettagliGenerali.reset({
            ...formDettagliGenerali.getValues(),
            anno: '',
            mese: '',
            modello: '',
            versione: '',
        });
        setMesi(monthOptions);
    }, [formDettagliGenerali, marca, monthOptions]);

    useEffect(() => {
        if (previousAnnoRef.current === undefined) {
            previousAnnoRef.current = anno;
            return;
        }
        if (previousAnnoRef.current === anno) return;
        previousAnnoRef.current = anno;
        if (!anno) {
            return;
        }
        formDettagliGenerali.reset({
            ...formDettagliGenerali.getValues(),
            modello: '',
            versione: '',
        });
    }, [anno, formDettagliGenerali]);

    useEffect(() => {
        if (previousModelloRef.current === undefined) {
            previousModelloRef.current = modello;
            return;
        }
        if (previousModelloRef.current === modello) return;
        previousModelloRef.current = modello;
        if (!modello) {
            return;
        }
        formDettagliGenerali.reset({
            ...formDettagliGenerali.getValues(),
            versione: '',
        });
    }, [formDettagliGenerali, modello]);

    const previousSelectedDateRef = useRef<Date | null | undefined>(undefined);

    useEffect(() => {
        if (previousSelectedDateRef.current === undefined) {
            previousSelectedDateRef.current = selectedDate;
            return;
        }
        previousSelectedDateRef.current = selectedDate;
        setSelectedTimeSlot("");
    }, [selectedDate]);

    useEffect(() => {
        permutaIdRef.current = permutaId;
    }, [permutaId]);

    useEffect(() => {
        codiceLeadRef.current = codiceLead;
    }, [codiceLead]);

    useEffect(() => {
        const clearRevealTimeout = () => {
            if (valutazioneRevealTimeoutRef.current !== null) {
                window.clearTimeout(valutazioneRevealTimeoutRef.current);
                valutazioneRevealTimeoutRef.current = null;
            }
        };

        if (currentStep < 4 || !usesEurotaxValutazioneFlow || !hasCompletedClientProfile) {
            clearRevealTimeout();
            setIsValutazioneRevealReady(false);
            return;
        }

        if (isValutazioneRevealReady || valutazioneRevealTimeoutRef.current !== null) {
            return;
        }

        const randomDelayMs = Math.floor(Math.random() * 5000) + 1;
        setIsValutazioneRevealReady(false);
        valutazioneRevealTimeoutRef.current = window.setTimeout(() => {
            setIsValutazioneRevealReady(true);
            valutazioneRevealTimeoutRef.current = null;
        }, randomDelayMs);

        return clearRevealTimeout;
    }, [currentStep, hasCompletedClientProfile, isValutazioneRevealReady, usesEurotaxValutazioneFlow]);

    useEffect(() => {
        if (!usesEurotaxValutazioneFlow) return;
        if (isManualValutazioneOverride) return;
        if (!hasPositiveQuotazioneProposta) return;

        formDatiCliente.setValue("valutazione", String(eurotaxValutazione?.quotazioneProposta), {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: true,
        });
    }, [eurotaxValutazione?.quotazioneProposta, formDatiCliente, hasPositiveQuotazioneProposta, isManualValutazioneOverride, usesEurotaxValutazioneFlow]);

    const stepNames: Record<number, string> = {
        1: "dettagli-generali",
        2: "richieste-veicolo",
        3: "condizioni-veicolo",
        4: "dati-cliente",
        5: "foto-auto",
    };


// Funzioni di validazione per ogni step
    const formValidators: Record<number, () => Promise<boolean>> = {
        1: () => formDettagliGenerali.trigger(),
        2: () => formRichiesteVeicolo.trigger(),
        3: () => formCondizioniVeicolo.trigger(),
        4: () => usesEurotaxValutazioneFlow
            ? formDatiCliente.trigger(["nome", "cognome", "targa", "telefono", "provincia"])
            : formDatiCliente.trigger(),
    };

    const sendPermutaMutation = useMutation({
        mutationFn: async (params: any): Promise<SendPermutaResponse> => {
            const sendPermutaUrl = `/api/permuta/send-permuta`;

            const response = await fetch(sendPermutaUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            });

            const data = await response.json().catch(() => ({}));
            const payload = data as SendPermutaResponse;

            if (!response.ok || payload.status === "error") {
                const error = new Error(data.message || "Errore nella risposta del server") as Error & { status?: number };
                error.status = response.status;
                throw error;
            }

            return payload;
        },
    });

    const getRuntimeLeadParams = () => {
        const queryParams = new URLSearchParams(window.location.search);
        return {
            gpic: queryParams.get('gpic') === 'true',
            idUtente: queryParams.get('idUtente') || '',
        };
    };

    const isGpicFlow = () => getRuntimeLeadParams().gpic;

    const buildPermutaPayload = ({
        step,
        includePhotos = false,
        includeAppuntamento = false,
    }: {
        step: string;
        includePhotos?: boolean;
        includeAppuntamento?: boolean;
    }) => {
        const {gpic, idUtente} = getRuntimeLeadParams();
        const currentPermutaId = permutaIdRef.current;
        const normalizedPermutaId = currentPermutaId
            ? (/^\d+$/.test(currentPermutaId) ? Number(currentPermutaId) : currentPermutaId)
            : null;
        const hasAlgorithmValutazione = typeof eurotaxValutazione?.quotazioneProposta === "number"
            && Number.isFinite(eurotaxValutazione.quotazioneProposta)
            && eurotaxValutazione.quotazioneProposta > 0;
        const hasRawQuotazioneProposta = typeof eurotaxValutazione?.quotazioneProposta === "number"
            && Number.isFinite(eurotaxValutazione.quotazioneProposta);
        const valutazioneAlgoritmo = hasAlgorithmValutazione
            ? !isManualValutazioneOverride
            : undefined;

        const params: any = {
            step,
            idPermuta: normalizedPermutaId,
            APERMUTA: aPermutaRef.current || null,
            codiceLead: codiceLeadRef.current || null,
            step1: {
                ...formDettagliGenerali.getValues(),
                fonte: getLeadSourceValue(),
                gpic,
                idUtente,
                marcaDesc: marche.find((marca: any) => marca.acronimo === formDettagliGenerali.getValues().marca)?.nome || '',
                modelloDesc: modelli.find((modello: any) => modello.gammaModello.codice === formDettagliGenerali.getValues().modello)?.gammaModello?.descrizione || '',
                versioneDesc: versioni.find((versione: any) => String(versione.codiceMotornetUnivoco ?? '') === String(formDettagliGenerali.getValues().versione ?? ''))?.nome || '',
            },
            step2: {...formRichiesteVeicolo.getValues()},
            step3: {...formCondizioniVeicolo.getValues()},
            step4: {
                ...formDatiCliente.getValues(),
                ...(hasRawQuotazioneProposta ? {quotazioneProposta: eurotaxValutazione?.quotazioneProposta} : {}),
                ...(valutazioneAlgoritmo !== undefined ? {valutazioneAlgoritmo} : {}),
            },
            photos: includePhotos ? photos.map((photo) => ({url: photo.url})) : [],
            appuntamento: includeAppuntamento ? {
                sedeId: selectedSede,
                sedeTitolo: sedi.find((s: any) => s._id === selectedSede)?.title || '',
                data: selectedDate ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}` : '',
                orario: selectedTimeSlot,
            } : {},
        };

        return params;
    };

    const submitPermutaPayload = async (params: any): Promise<boolean> => {
        if (isSubmittingRef.current || isSubmitting) return false;
        isSubmittingRef.current = true;
        setIsSubmitting(true);

        try {
            const payload = await sendPermutaMutation.mutateAsync(params);

            if (payload.status !== "success") {
                throw new Error(payload.message || "Errore nella risposta del server");
            }

            const result = payload.data ?? {};
            const nextPermutaId =
                result.idPermuta !== undefined && result.idPermuta !== null
                    ? String(result.idPermuta)
                    : "";

            if (!nextPermutaId) {
                throw new Error("Permuta creata senza idPermuta");
            }

            const nextProductId =
                result.productId !== undefined && result.productId !== null
                    ? String(result.productId)
                    : productId;
            const nextAPermuta =
                result.APERMUTA !== undefined && result.APERMUTA !== null
                    ? String(result.APERMUTA).trim()
                    : aPermutaRef.current;
            const rawCodiceLead =
                typeof result.codiceLead === "string"
                    ? result.codiceLead.trim()
                    : "";
            const nextCodiceLead = rawCodiceLead || codiceLeadRef.current;

            if (!rawCodiceLead) {
                console.warn("send-permuta succeeded but codiceLead is missing", {
                    idPermuta: nextPermutaId,
                    productId: nextProductId || null,
                });
            }

            setProductId(nextProductId);
            setPermutaId(nextPermutaId);
            setCodiceLead(nextCodiceLead);
            permutaIdRef.current = nextPermutaId;
            aPermutaRef.current = nextAPermuta;
            codiceLeadRef.current = nextCodiceLead;
            setValutezione((result.valutazioneStimata as { min: number; max: number } | null) ?? null);
            return true;
        } catch (error) {
            console.error("Errore durante la richiesta:", error);

            const errorWithStatus = error as Error & { status?: number };
            if (errorWithStatus.status === 409) {
                setDialogMessage("Hai già inviato una permuta recentemente. Attendi prima di inviare nuovamente.");
                setOpenDialog(true);
                return false;
            }

            const errorMessage = error instanceof Error ? error.message : "Si è verificato un errore, riprova più tardi.";
            setDialogMessage(errorMessage);
            setOpenDialog(true);
            return false;
        } finally {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    const createLeadAtStep4 = async (): Promise<boolean> => {
        const params = buildPermutaPayload({
            step: stepNames[4] || "dati-cliente",
            includePhotos: false,
            includeAppuntamento: false,
        });
        return submitPermutaPayload(params);
    };

    const updateLeadAfterStep = async ({
        step = stepNames[4] || "dati-cliente",
        includePhotos = false,
        includeAppuntamento = false,
    }: {
        step?: string;
        includePhotos?: boolean;
        includeAppuntamento?: boolean;
    } = {}): Promise<boolean> => {
        if (!permutaIdRef.current) {
            const isCreated = await createLeadAtStep4();
            if (!isCreated) return false;
        }

        const params = buildPermutaPayload({
            step,
            includePhotos,
            includeAppuntamento,
        });
        return submitPermutaPayload(params);
    };

    const redirectToThankYou = async () => {
        const emailValue = formDettagliGenerali.getValues().email;
        const nomeValue = formDatiCliente.getValues().nome;
        const cognomeValue = formDatiCliente.getValues().cognome;
        const telefonoValue = formDatiCliente.getValues().telefono;
        const quotazioneProposta = eurotaxValutazione?.quotazioneProposta;
        const hasAutomaticValutazione = typeof quotazioneProposta === "number"
            && Number.isFinite(quotazioneProposta)
            && quotazioneProposta > 0
            && !isManualValutazioneOverride;
        const payload = {
            permutaId: permutaIdRef.current || undefined,
            productId: productId || undefined,
            APERMUTA: aPermutaRef.current || undefined,
            codiceLead: codiceLeadRef.current || undefined,
            permutaLeadCode: codiceLeadRef.current || undefined,
            valoreDesiderato: formDatiCliente.getValues().valutazione || undefined,
            targa: formDatiCliente.getValues().targa || undefined,
            marca: formDettagliGenerali.getValues().marca || undefined,
            modello: formDettagliGenerali.getValues().modello || undefined,
            versione: formDettagliGenerali.getValues().versione || undefined,
            meseImmatricolazione: formDettagliGenerali.getValues().mese || undefined,
            annoImmatricolazione: formDettagliGenerali.getValues().anno || undefined,
            km: formDettagliGenerali.getValues().chilometri || undefined,
            valutazioneStimata: valutezione ?? undefined,
            permutaCompleted: true,
        };

        if (embeddedMode) {
            onCompleted?.(payload);
            return;
        }

        formDettagliGenerali.reset();
        formRichiesteVeicolo.reset();
        formCondizioniVeicolo.reset();
        formDatiCliente.reset();
        setPhotos([]);
        setSelectedSede("");
        setSelectedDate(null);
        setSelectedTimeSlot("");
        setPermutaId("");
        setCodiceLead("");
        permutaIdRef.current = "";
        aPermutaRef.current = "";
        codiceLeadRef.current = "";
        setProductId("");
        setValutezione(null);
        setEurotaxValutazione(null);
        setIsManualValutazioneOverride(false);

        const thankYouParams = new URLSearchParams({
            email: emailValue,
            nome: nomeValue,
            telefono: telefonoValue,
        });

        if (hasAutomaticValutazione) {
            thankYouParams.set("cognome", cognomeValue);
            thankYouParams.set("valutazione", String(quotazioneProposta));
            await router.push(`/thank-you-page/permuta?${thankYouParams.toString()}`);
        } else if (permuta === "Permuta") {
            await router.push(`/thank-you-page/permuta?${thankYouParams.toString()}`);
        } else {
            await router.push(`/thank-you-page/vendi-la-tua-auto?${thankYouParams.toString()}`);
        }
    };


    const nextStep = async () => {
        if (formValidators[currentStep]) {
            const isValid = await formValidators[currentStep]();
            if (!isValid) return;
        }

        let targetStep: number;

        if (currentStep === 4) {
            if (isGpicFlow()) {
                const isLeadSaved = permutaIdRef.current ? await updateLeadAfterStep() : await createLeadAtStep4();
                if (!isLeadSaved) return;
                await redirectToThankYou();
                return;
            } else if (permuta === "Permuta") {
                targetStep = 5;
            } else {
                await handleFinalSubmit({includePhotos: false});
                return;
            }
        } else if (currentStep === 5) {
            if (minNumberOfPhotos > 0 && photos.length < minNumberOfPhotos) {
                setDialogMessage(`Carica almeno ${minNumberOfPhotos} foto prima di continuare.`);
                setOpenDialog(true);
                return;
            }
            if (photos.some(p => p.isLoading)) {
                setDialogMessage("Attendi il completamento del caricamento delle foto.");
                setOpenDialog(true);
                return;
            }

            if (isGpicFlow()) {
                const isLeadUpdated = await updateLeadAfterStep({step: stepNames[5] || "foto", includePhotos: true});
                if (!isLeadUpdated) return;
                await redirectToThankYou();
                return;
            }

            await handleFinalSubmit({includePhotos: true});
            return;
        } else {
            targetStep = Math.min(currentStep + 1, finalStep);
        }

        const stepName = stepNames[targetStep] || "dati-cliente";
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set("step", stepName);
        window.history.pushState({}, "", newUrl.toString());
        window.scrollTo({top: 0, behavior: "smooth"});
        setCurrentStep(targetStep);
    };


    const prevStep = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 1));
    };

    const [step, setStep] = useState(1);

    useEffect(() => {
        if (marca) setStep(1);
        if (anno) setStep(2);
        if (mese) setStep(3);
        if (modello) setStep(4);
        if (versione) setStep(5);
        if (chilometri) setStep(6);
        if (permuta) setStep(7);
        if (email) setStep(8);
    }, [marca, anno, mese, modello, versione, chilometri, permuta, email]);

    return (
        <section id="form-permuta" className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm md:p-6">
            {eurotaxWarning ? (
                <Alert className="mb-4 border-amber-200 bg-amber-50 text-amber-900">
                    <AlertDescription>{eurotaxWarning}</AlertDescription>
                </Alert>
            ) : null}


            {/*
            <h5 className="text-xl font-bold pb-5">{title}</h5>
*/}
            {currentStep === 1 && (
                <StepDettagliGenerali
                    formDettagliGenerali={formDettagliGenerali}
                    step={step}
                    veichleType={veichleType}
                    setVeichleType={setVeichleType}
                    marche={marche}
                    anni={anni}
                    marca={marca}
                    mesi={mesi}
                    anno={anno}
                    modelli={modelli}
                    mese={mese}
                    versioni={versioni}
                    modello={modello}
                    versione={versione}
                    chilometri={chilometri}
                    permuta={permuta}
                    hidePermutaChoice={forcePermutaOnly}
                />
            )}

            {currentStep === 2 && <StepRichiesteVeicolo formRichiesteVeicolo={formRichiesteVeicolo} />}

            {currentStep === 3 && <StepCondizioniVeicolo formCondizioniVeicolo={formCondizioniVeicolo} />}

            {currentStep === 4 && (
                <StepDatiCliente
                    formDatiCliente={formDatiCliente}
                    formDettagliGenerali={formDettagliGenerali}
                    eurotaxValutazione={eurotaxValutazione}
                    isValutazioneLoading={hasCompletedClientProfile && (!isValutazioneRevealReady || eurotaxValutazioneQuery.isLoading)}
                    isClienteProfileComplete={hasCompletedClientProfile}
                    isManualValutazioneOverride={isManualValutazioneOverride}
                    onEnableValutazioneOverride={() => setIsManualValutazioneOverride(true)}
                    onDisableValutazioneOverride={() => setIsManualValutazioneOverride(false)}
                    showValutazioneSection={usesEurotaxValutazioneFlow && !shouldHideValutazioneSection}
                />
            )}

            {currentStep === 5 && (
                <StepFotoAuto
                    minNumberOfPhotos={minNumberOfPhotos}
                    getRootProps={getRootProps}
                    getInputProps={getInputProps}
                    isDragActive={isDragActive}
                    photos={photos}
                    removePhoto={removePhoto}
                />
            )}

            <div className="col-span-2 mt-6 flex justify-between border-t border-gray-200 pt-6">
                <div className="flex flex-col justify-center items-center w-full">
                    {currentStep < finalStep ? (
                            <Button
                                onClick={nextStep}
                                disabled={
                                    isSubmitting ||
                                    (
                                        currentStep === 4 &&
                                        permuta !== "Permuta" &&
                                        usesEurotaxValutazioneFlow &&
                                        hasCompletedClientProfile &&
                                        (!isValutazioneRevealReady || eurotaxValutazioneQuery.isLoading)
                                    )
                                }
                                className="mt-4 w-full text-white sm:min-w-64 sm:w-auto">
                                Prosegui
                                <ChevronRight className="h-5 w-5 ml-2"/>
                            </Button>
                        )
                        :
                        (
                            <Button
                                onClick={() => handleFinalSubmit({includePhotos: permuta === "Permuta"})}
                                disabled={
                                    isSubmitting ||
                                    (
                                        usesEurotaxValutazioneFlow &&
                                        hasCompletedClientProfile &&
                                        (!isValutazioneRevealReady || eurotaxValutazioneQuery.isLoading)
                                    )
                                }
                                className="w-full text-white sm:min-w-64 sm:w-auto"
                            >
                                {isSubmitting ? 'Invio in corso...' : 'Invia'}
                            </Button>
                        )
                    }
                    {
                        currentStep > 1 && (
                            <Button onClick={prevStep}
                                    className="bg-transparent text-gray-500 underline hover:bg-transparent">indietro</Button>
                        )
                    }
                </div>
            </div>

            <Dialog open={openDialog} onOpenChange={setOpenDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Attenzione</DialogTitle>
                        <DialogDescription>{dialogMessage}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button onClick={() => setOpenDialog(false)}>Chiudi</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    )

};

export default FormPermuta;
